import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from './api';
import { useAuth } from '@/lib/auth/useAuth';
import { classifyLoginError, type LoginStep } from './loginError';
import { takePendingConsent } from '@/features/oauth/pendingConsent';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { ONE_TIME_CODE_LENGTH, OneTimeCodeInput } from '@/lib/ui/OneTimeCodeInput';

/** Which field takes focus when a step opens. */
type FocusTarget = 'email' | 'password' | 'code';

/**
 * Login + conditional MFA (spec §3.1), as two steps. Email and password first; when the
 * server says the account needs an authenticator code, the credentials step is replaced by a
 * verification step that shows who is signing in — read-only, with a way back to change
 * account — and asks only for the code. The password stays in memory for the second request
 * (the login endpoint verifies both together) and is dropped as soon as the user goes back.
 * Lockout and rate-limit get specific messages. Deep-link preserved.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { setSession, pendingDeepLink } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Auto-submit on the sixth digit can race a click on "Verify"; a ref sees the in-flight
  // request before React re-renders with `loading`.
  const inFlight = useRef(false);
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!focusTarget) return;
    const el = { email: emailRef, password: passwordRef, code: codeRef }[focusTarget].current;
    el?.focus();
    if (focusTarget === 'email') el?.select();
    setFocusTarget(null);
  }, [focusTarget, step]);

  async function attempt(totpCode?: string) {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.login({
        email: email.trim().toLowerCase(),
        password,
        totp_code: totpCode,
      });
      await setSession(tokens);
      navigate(takePendingConsent() ?? pendingDeepLink ?? '/');
    } catch (err) {
      const next = classifyLoginError(err, step);
      setError(next.message && t(next.message));
      if (next.step === 'code') {
        // A rejected code is spent; clear it so the next one is typed into an empty field.
        setCode('');
        setFocusTarget('code');
      } else if (step === 'code') {
        // Wrong password, lockout or disabled answered from the code step: back to where
        // it can be dealt with, without the stale password.
        setPassword('');
        setCode('');
        setFocusTarget('password');
      }
      setStep(next.step);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }

  function useDifferentAccount() {
    setStep('credentials');
    setPassword('');
    setCode('');
    setError(null);
    setFocusTarget('email');
  }

  return (
    <AuthLayout>
      {step === 'credentials' ? (
        <CredentialsStep
          email={email}
          password={password}
          onEmail={setEmail}
          onPassword={setPassword}
          emailRef={emailRef}
          passwordRef={passwordRef}
          error={error}
          loading={loading}
          onSubmit={() => void attempt()}
        />
      ) : (
        <CodeStep
          email={email.trim()}
          code={code}
          onCode={setCode}
          codeRef={codeRef}
          error={error}
          loading={loading}
          onSubmit={(value) => void attempt(value)}
          onUseDifferentAccount={useDifferentAccount}
        />
      )}
    </AuthLayout>
  );
}

function CredentialsStep({
  email,
  password,
  onEmail,
  onPassword,
  emailRef,
  passwordRef,
  error,
  loading,
  onSubmit,
}: {
  email: string;
  password: string;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  emailRef: React.RefObject<HTMLInputElement>;
  passwordRef: React.RefObject<HTMLInputElement>;
  error: string | null;
  loading: boolean;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit();
  }
  return (
    <form onSubmit={submit} className="auth-step flex flex-col gap-md">
      <h1 className="mb-xs text-headline">{t('auth.signIn')}</h1>
      <TextInput
        ref={emailRef}
        label={t('auth.email')}
        type="email"
        autoComplete="username"
        required
        autoFocus
        value={email}
        onChange={(e) => onEmail(e.target.value)}
      />
      <TextInput
        ref={passwordRef}
        label={t('auth.password')}
        type="password"
        autoComplete="current-password"
        required
        value={password}
        onChange={(e) => onPassword(e.target.value)}
      />
      {error && (
        <p className="text-small text-signal-error" role="alert">
          {error}
        </p>
      )}
      <Button type="submit" loading={loading} className="mt-xs">
        {t('auth.signIn')}
      </Button>
      <Link
        to="/login/forgot"
        className="self-start text-small text-accent-blue underline-offset-4 hover:text-ink-deep hover:underline"
      >
        {t('auth.forgot')}
      </Link>
    </form>
  );
}

function CodeStep({
  email,
  code,
  onCode,
  codeRef,
  error,
  loading,
  onSubmit,
  onUseDifferentAccount,
}: {
  email: string;
  code: string;
  onCode: (v: string) => void;
  codeRef: React.RefObject<HTMLInputElement>;
  error: string | null;
  loading: boolean;
  onSubmit: (code: string) => void;
  onUseDifferentAccount: () => void;
}) {
  const { t } = useTranslation();
  const leadId = useId();
  const errorId = useId();
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit(code);
  }
  return (
    <form onSubmit={submit} className="auth-step flex flex-col gap-md">
      <h1 className="text-headline">{t('auth.codeTitle')}</h1>

      <div className="flex flex-col items-start gap-2xs rounded-sm border-[0.5px] border-hairline bg-canvas-bone px-[14px] py-sm">
        <p className="text-small text-ink-mid">{t('auth.signingInAs')}</p>
        <p
          className="max-w-full font-medium text-ink-graphite [overflow-wrap:anywhere]"
          dir="ltr"
          translate="no"
        >
          {email}
        </p>
        <button
          type="button"
          onClick={onUseDifferentAccount}
          className="mt-2xs rounded-xs text-small text-accent-blue underline-offset-4 hover:text-ink-deep hover:underline focus-visible:shadow-focus focus-visible:outline-none"
        >
          {t('auth.useDifferentAccount')}
        </button>
      </div>

      <p id={leadId} className="mt-xs text-small text-ink-mid">
        {t('auth.codeLead')}
      </p>

      <OneTimeCodeInput
        ref={codeRef}
        label={t('auth.totp')}
        required
        autoFocus
        value={code}
        onChange={onCode}
        onComplete={onSubmit}
        readOnly={loading}
        className={error ? 'border-signal-error' : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${leadId} ${errorId}` : leadId}
      />
      {error && (
        <p id={errorId} className="text-small text-signal-error" role="alert">
          {error}
        </p>
      )}
      <Button
        type="submit"
        loading={loading}
        disabled={code.length !== ONE_TIME_CODE_LENGTH}
        className="mt-xs"
      >
        {t('auth.verify')}
      </Button>
      <p className="text-small text-ink-mute">{t('auth.codeHelp')}</p>
    </form>
  );
}

/**
 * The frame every signed-out screen shares (sign-in, password reset, OAuth consent and its
 * errors): a paper sheet on the navy field, headed by the console's name so the page says
 * where the visitor is before any heading asks them for something.
 */
export function AuthLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-navy p-md sm:p-lg">
      <main className="w-full max-w-md rounded-lg border-[0.5px] border-hairline bg-canvas-paper px-lg py-xl sm:p-2xl">
        <div className="mb-xl flex items-center gap-sm">
          <svg viewBox="0 0 32 32" aria-hidden className="h-7 w-7 shrink-0">
            <rect width="32" height="32" rx="6" fill="var(--ink-navy)" />
            <path
              d="M8 10h16M16 10v13"
              stroke="var(--canvas-paper)"
              strokeWidth="2.6"
              strokeLinecap="square"
            />
          </svg>
          <p className="flex items-baseline gap-xs text-small">
            <span className="font-medium text-ink-deep">TEPCO KSA</span>
            <span className="text-ink-mute">{t('auth.console')}</span>
          </p>
        </div>
        {children}
      </main>
    </div>
  );
}
